import React, {
    createContext,
    useContext,
    useCallback,
    useState,
    useRef,
    useMemo,
} from "react";
import type {
    QueueContextValue,
    selectedArea,
    VisibleFeature,
} from "../types/types";
import { useQueueFilters } from "../component/hooks/useQueueFilters";
import {
    isTransmissionLineFeature,
    transformTransmissionLineFeature,
    TransmissionLineDisplay,
    TransmissionLineFeature,
} from "../component/map/TransmissionLineLayerColumns";
import {
    isSubstationFeature,
    transformSubstationFeature,
    SubstationDisplay,
    SubstationFeature,
} from "../component/map/SubstationLayerColumns";
import {
    isGeneratorFeature,
    transformGeneratorFeature,
    GeneratorDisplay,
    GeneratorFeature,
} from "../component/map/GeneratorLayerColumns";
import {
    isQueueIsoFeature,
    transformQueueIsoFeature,
    QueueIsoDisplay,
    QueueIsoFeature,
} from "../component/map/QueueIsoColumns";
import useQueueData from "../component/hooks/useQueueData";

export type TableFilterOptions = "all" | "selected" | "visible";

export type GeoJSONFeature = {
    id?: string | number;
    type: string;
    properties: {
        [key: string]: any;
    };
    geometry: {
        type: string;
        coordinates: number[][];
    };
};

type FeatureTypeMap = {
    transmissionLine: {
        feature: TransmissionLineFeature;
        display: TransmissionLineDisplay;
        check: (feature: VisibleFeature) => feature is TransmissionLineFeature;
        transform: (
            feature: TransmissionLineFeature
        ) => TransmissionLineDisplay;
    };
    substation: {
        feature: SubstationFeature;
        display: SubstationDisplay;
        check: (feature: VisibleFeature) => feature is SubstationFeature;
        transform: (feature: SubstationFeature) => SubstationDisplay;
    };
    generator: {
        feature: GeneratorFeature;
        display: GeneratorDisplay;
        check: (feature: VisibleFeature) => feature is GeneratorFeature;
        transform: (feature: GeneratorFeature) => GeneratorDisplay;
    };
    queueIso: {
        feature: QueueIsoFeature;
        display: QueueIsoDisplay;
        check: (feature: VisibleFeature) => feature is QueueIsoFeature;
        transform: (feature: QueueIsoFeature) => QueueIsoDisplay;
    };
};

const featureHandlers: {
    [K in keyof FeatureTypeMap]: {
        check: FeatureTypeMap[K]["check"];
        transform: FeatureTypeMap[K]["transform"];
    };
} = {
    transmissionLine: {
        check: isTransmissionLineFeature,
        transform: transformTransmissionLineFeature,
    },
    substation: {
        check: isSubstationFeature,
        transform: transformSubstationFeature,
    },
    generator: {
        check: isGeneratorFeature,
        transform: transformGeneratorFeature,
    },
    queueIso: {
        check: isQueueIsoFeature,
        transform: transformQueueIsoFeature,
    },
} as const;

const QueueInsightsContext = createContext<QueueContextValue | undefined>(
    undefined
);

export const QueueInsightsProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { queueData, loading: queueDataLoading } = useQueueData("QueueInsightsISO");
    
    const [mapState, setMapState] = useState({
        visibleFeatures: [] as VisibleFeature[],
        allFeatures: [] as VisibleFeature[],
        selectedArea: null as selectedArea | null,
    });
    
    const queueFilters = useQueueFilters({ queueData, queueDataLoading, selectedArea: mapState.selectedArea || undefined });

    const mapInstanceRef = useRef<mapboxgl.Map | null>(null);

    const updateVisibleFeatures = useCallback((features: VisibleFeature[]) => {
        setMapState((prev) => {
            // Add new visible features to allFeatures
            const existingIds = new Set(prev.allFeatures.map((f) => f.id));
            const newFeatures = features.filter((f) => !existingIds.has(f.id));

            return {
                ...prev,
                visibleFeatures: features,
                allFeatures: [...prev.allFeatures, ...newFeatures],
            };
        });
    }, []);

    const updateSelectedArea = useCallback(
        (
            bounds: [[number, number], [number, number]] | null,
            features: VisibleFeature[],
            geometry?: GeoJSON.Feature | null,
            mode?: "Rectangle" | "Circle" | "Lasso" | null
        ) => {
            setMapState((prev) => ({
                ...prev,
                selectedArea: {
                    bounds,
                    features,
                    geometry: geometry || prev.selectedArea?.geometry || null,
                    mode: mode || prev.selectedArea?.mode || null,
                },
            }));
        },
        []
    );

    const clearSelectedArea = useCallback(() => {
        setMapState((prev) => ({
            ...prev,
            selectedArea: {
                bounds: null,
                features: [],
                geometry: null,
                mode: null,
            },
        }));
    }, []);

    const value: QueueContextValue = useMemo(
        () => ({
            isLoading: queueDataLoading,
            ...queueFilters,

            /* Map-selection state & helpers */
            ...mapState,
            updateVisibleFeatures,
            updateSelectedArea,
            clearSelectedArea,
            mapInstanceRef,
        }),
        [
            queueFilters,
            queueDataLoading,
            mapState,
            updateVisibleFeatures,
            updateSelectedArea,
            clearSelectedArea,
            mapInstanceRef
        ]
    );

    return (
        <QueueInsightsContext.Provider value={value}>
            {children}
        </QueueInsightsContext.Provider>
    );
};

export const useQueueInsights = () => {
    const context = useContext(QueueInsightsContext);
    if (context === undefined) {
        throw new Error(
            "useQueueInsights must be used within a QueueInsightsProvider"
        );
    }
    return context;
};

export const useVisibleFeatures = () => {
    const { visibleFeatures } = useQueueInsights();

    const getFeaturesByType = useCallback(
        (type: VisibleFeature["type"]) => {
            return visibleFeatures.filter((feature) => feature.type === type);
        },
        [visibleFeatures]
    );

    const getFeaturesByArea = useCallback(
        (bounds: [[number, number], [number, number]]) => {
            return visibleFeatures.filter((feature) => {
                const [lng, lat] = feature.geometry.coordinates as [
                    number,
                    number,
                ];
                const [[minLng, minLat], [maxLng, maxLat]] = bounds;
                return (
                    lng >= minLng &&
                    lng <= maxLng &&
                    lat >= minLat &&
                    lat <= maxLat
                );
            });
        },
        [visibleFeatures]
    );

    return {
        allFeatures: visibleFeatures,
        getFeaturesByType,
        getFeaturesByArea,
    };
};

export function useFeatureData<T extends keyof FeatureTypeMap>(
    featureType: T,
    filterOption: TableFilterOptions = "visible"
): FeatureTypeMap[T]["display"][] {
    const { visibleFeatures, selectedArea, allFeatures } = useQueueInsights();

    return useMemo(() => {
        let features = [];

        switch (filterOption) {
            case "all":
                features = allFeatures;
                break;
            case "visible":
                features = visibleFeatures;
                break;
            case "selected":
                features = selectedArea?.features || [];
                break;
            default:
                features = visibleFeatures;
        }

        const { check, transform } = featureHandlers[featureType];

        const checkedFeatures = features.filter(check) as Array<
            FeatureTypeMap[T]["feature"]
        >;

        return checkedFeatures.map((feature: any) => transform(feature));
    }, [
        featureType,
        filterOption,
        visibleFeatures,
        selectedArea?.features,
        allFeatures,
    ]);
}

export const useTransmissionLineData = (
    filterOption: TableFilterOptions = "all"
) => useFeatureData("transmissionLine", filterOption);

export const useSubstationData = (filterOption: TableFilterOptions = "all") =>
    useFeatureData("substation", filterOption);

export const useGeneratorData = (filterOption: TableFilterOptions = "all") =>
    useFeatureData("generator", filterOption);

export const useQueueIsoData = (filterOption: TableFilterOptions = "all") =>
    useFeatureData("queueIso", filterOption);